import React, { useEffect, useLayoutEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { S } from './shellTokens';

type Box = { top: number; left: number; width: number; height: number };

type Props = {
  /** The selected top-level section (its data-gx-component id). */
  sectionId: string | null;
  label: string;
  /** The page sheet the marks are drawn in (position: relative). */
  sheet: HTMLElement | null;
  /** Changes when the page content changes, to measure again. */
  layoutKey?: unknown;
};

const MARK = 14;
const GAP = 5;

const useStyles = createUseStyles({
  frame: { position: 'absolute', pointerEvents: 'none', zIndex: 5, boxShadow: `0 0 0 1px rgba(23, 26, 32, 0.35)` },
  mark: { position: 'absolute', width: MARK, height: MARK, borderColor: S.fg, borderStyle: 'solid', borderWidth: 0 },
  tl: { top: -GAP, left: -GAP, borderTopWidth: 2, borderLeftWidth: 2, transform: 'translate(-2px, -2px)' },
  tr: { top: -GAP, right: -GAP, borderTopWidth: 2, borderRightWidth: 2, transform: 'translate(2px, -2px)' },
  bl: { bottom: -GAP, left: -GAP, borderBottomWidth: 2, borderLeftWidth: 2, transform: 'translate(-2px, 2px)' },
  br: { bottom: -GAP, right: -GAP, borderBottomWidth: 2, borderRightWidth: 2, transform: 'translate(2px, 2px)' },
  // No wider than the section: a long name ends with "…", and the size always shows.
  label: {
    position: 'absolute',
    left: 0,
    display: 'flex',
    maxWidth: '100%',
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
    fontFamily: S.mono,
    fontSize: 11,
    lineHeight: '18px',
    padding: '0 6px',
    borderRadius: 3,
    background: S.fg,
    color: S.field
  },
  labelName: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' },
  labelSize: { flexShrink: 0 }
});

const measure = (el: Element, sheet: HTMLElement): Box => {
  const r = el.getBoundingClientRect();
  const s = sheet.getBoundingClientRect();
  return { top: r.top - s.top, left: r.left - s.left, width: r.width, height: r.height };
};

const selectorFor = (id: string) => `[data-gx-component="${window.CSS && window.CSS.escape ? window.CSS.escape(id) : id}"]`;

const same = (a: Box | null, b: Box | null) => Boolean(a && b && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height);

/**
 * Crop marks around the selected section with its name and rendered size,
 * like a selection on a drawing board. Drawn over the page, never clickable.
 */
const SelectionMarks: React.FC<Props> = ({ sectionId, label, sheet, layoutKey }) => {
  const c = useStyles();
  const [box, setBox] = useState<Box | null>(null);

  useLayoutEffect(() => {
    if (!sectionId || !sheet) { setBox(null); return undefined; }
    const find = () => sheet.querySelector(selectorFor(sectionId));
    const update = () => {
      const el = find();
      const next = el ? measure(el, sheet) : null;
      setBox(prev => (same(prev, next) ? prev : next));
    };
    update();
    // Sections above can grow (images, scripts), which moves this one: watch the sheet too.
    const el = find();
    const RO = (window as unknown as { ResizeObserver?: typeof ResizeObserver }).ResizeObserver;
    const observer = RO ? new RO(update) : null;
    if (observer) {
      observer.observe(sheet);
      if (el) observer.observe(el);
    }
    window.addEventListener('resize', update);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [sectionId, sheet, layoutKey]);

  // A late-loading section (lazy widget code) appears after the first measure.
  useEffect(() => {
    if (!sectionId || !sheet || box) return undefined;
    const t = window.setTimeout(() => {
      const el = sheet.querySelector(selectorFor(sectionId));
      if (el) setBox(measure(el, sheet));
    }, 500);
    return () => window.clearTimeout(t);
  }, [sectionId, sheet, box]);

  if (!box || !sectionId) return null;
  const labelInside = box.top < 22;
  return (
    <div className={c.frame} style={box} data-testid="selection-marks" aria-hidden>
      <span className={`${c.mark} ${c.tl}`} />
      <span className={`${c.mark} ${c.tr}`} />
      <span className={`${c.mark} ${c.bl}`} />
      <span className={`${c.mark} ${c.br}`} />
      <span className={c.label} style={labelInside ? { top: 6, left: 6, maxWidth: 'calc(100% - 12px)' } : { top: -24 }} data-testid="selection-label">
        <span className={c.labelName}>{label}</span>
        <span className={c.labelSize}>&nbsp;· {Math.round(box.width)} × {Math.round(box.height)}</span>
      </span>
    </div>
  );
};

export default SelectionMarks;
